import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { PricingPlanFeatureKey } from '@/constants/pricing.constants';
type RichTranslationValues = Record<string, (chunks: ReactNode) => ReactNode>;

/**
 * Hook for accessing pricing feature translations with dynamic keys.
 * Handles optional properties (tag, tooltip) that may not exist on all features.
 *
 * Note: Dynamic template literal keys cannot be fully type-checked by TypeScript
 * when combined with next-intl's strict typing. This hook provides runtime safety
 * through PricingPlanFeatureKey validation while working around TypeScript limitations.
 */
export function usePricingFeatureTranslations() {
  const $ = _c(2);
  const t = useTranslations("FeatureComparisonTable");
  let t0;
  if ($[0] !== t) {
    t0 = {
      getLabel: key => t(`features.${key}.label` as never),
      getTooltip: key_0 => t(`features.${key_0}.tooltip` as never),
      getTag: key_1 => t(`features.${key_1}.tag` as never),
      hasTooltip: key_2 => t.has(`features.${key_2}.tooltip` as never),
      hasTag: key_3 => t.has(`features.${key_3}.tag` as never),
      richLabel: (key_4, values) => t.rich(`features.${key_4}.label` as never, values as never),
      rawTooltip: key_5 => t.raw(`features.${key_5}.tooltip` as never)
    };
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}